import { useCallback, useState } from 'react';
import type { Rates, SubItemTypeMetaMap } from '@jobos/shared';
import type { FlatItem } from '@/pages/jobs/components/breakdown/types';
import type { LineItemData, ProjectData } from '@/pages/jobs/components/breakdown/LineItemList';
import { createSubItem, type SubItemForm } from '@/pages/jobs/components/breakdown/subItemTypes';
import { useFlatItems } from '@/pages/jobs/components/breakdown/useFlatItems';

/**
 * In-memory editor state for the job-type breakdown TEMPLATE editor.
 *
 * This is a deliberately stripped sibling of `JobDetailBreakdown`'s editor
 * state. It owns the SAME shape — `flatItems` (loose line items + projects),
 * `rates` ({ taxRate, markupRate }), `roundingPrecision`, and selection — and
 * exposes the SAME pure state-transform handlers the breakdown DnD widget
 * expects. Everything tied to a job/document is removed: no autosave, undo,
 * offline persistence, granular PATCH, bulk-checkbox, copy/paste, export,
 * invoice, version, or query-cache logic. Mutations just transform local
 * state and flip a `dirty` flag for the edit page to consume.
 *
 * The flat-array transforms and DnD reordering machinery are reused verbatim
 * from `useFlatItems` (which has no job/document coupling of its own); this
 * hook composes it and adds rates / rounding / selection / dirty on top.
 *
 * NOTE on totalCost: in `JobDetailBreakdown`, several handlers recompute a line
 * item's `totalCost` via `calculateTotalCost(subItems, rates, typeMeta,
 * categories)`. That needs the sub-item category context, which is NOT part of
 * this hook's signature, so those recomputations are intentionally omitted here
 * — `totalCost` is left untouched on edits. The breakdown widget computes live
 * display totals from sub-items via its `computeItemTotal` prop, so the editor
 * still shows correct totals; the persisted value is recomputed on save.
 * Likewise the category-defaults auto-population on add/category-change is
 * omitted (it depends on the same context).
 */
export function useTemplateBreakdownState(
  initialFlat: FlatItem[],
  initialRates: Rates,
  initialRounding: number | null,
  typeMeta: SubItemTypeMetaMap,
) {
  const {
    flatItems,
    collapsedProjects,
    initialize,
    moveItem,
    moveItemToEnd,
    moveProject,
    toggleCollapse,
    getVisibleSortableIds,
    startProjectDrag,
    cancelProjectDrag,
    addLineItem: flatAddLineItem,
    deleteLineItem: flatDeleteLineItem,
    updateLineItem: flatUpdateLineItem,
    addProject: flatAddProject,
    deleteProject: flatDeleteProject,
    renameProject: flatRenameProject,
  } = useFlatItems();

  const [rates, setRates] = useState<Rates>(initialRates);
  const [roundingPrecision, setRoundingPrecision] = useState<number | null>(initialRounding);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedSubItemId, setSelectedSubItemId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Seed the flat items on first render. `initialize` is idempotent-ish here:
  // we only seed once, on mount, mirroring JobDetailBreakdown's initial-load
  // effect. Subsequent re-seeds go through `reset`.
  const [seeded, setSeeded] = useState(false);
  if (!seeded) {
    initialize(initialFlat);
    setSeeded(true);
  }

  // --------------------------------------------------------------------------
  // reset — re-seed all state (called after a successful save, or when the
  // loaded template changes). Clears dirty.
  // --------------------------------------------------------------------------
  const reset = useCallback(
    (nextFlat: FlatItem[], nextRates: Rates, nextRounding: number | null) => {
      initialize(nextFlat);
      setRates(nextRates);
      setRoundingPrecision(nextRounding);
      setSelectedItemId(null);
      setSelectedSubItemId(null);
      setDirty(false);
    },
    [initialize],
  );

  // --------------------------------------------------------------------------
  // markSaved — clear dirty WITHOUT re-seeding (called after a successful save).
  // Keeps current edits in place so the editor's dirty effect fires
  // onDirtyChange(false) and subsequent edits correctly re-fire it.
  // --------------------------------------------------------------------------
  const markSaved = useCallback(() => setDirty(false), []);

  // --------------------------------------------------------------------------
  // handleToggleExpand — select/expand a line item, or collapse it when it's
  // already the selected one. Mirrors JobDetailBreakdown.handleToggleExpand:
  // the caret/row wires to this (not the raw setter) so clicking an expanded
  // item collapses it. Clears the selected sub-item on every toggle.
  // --------------------------------------------------------------------------
  const handleToggleExpand = useCallback((id: string | null) => {
    if (id === null) {
      setSelectedItemId(null);
    } else {
      setSelectedItemId((current) => (current === id ? null : id));
    }
    setSelectedSubItemId(null);
  }, []);

  // --------------------------------------------------------------------------
  // Line item handlers
  // --------------------------------------------------------------------------
  const handleAddLineItem = useCallback(() => {
    const newItemId = crypto.randomUUID();
    const newItem: LineItemData = {
      id: newItemId,
      category: '',
      description: '',
      totalCost: 0,
      subItems: [],
    };
    flatAddLineItem(newItem, undefined, selectedItemId ?? undefined);
    setSelectedItemId(newItemId);
    setDirty(true);
  }, [flatAddLineItem, selectedItemId]);

  const handleDeleteLineItem = useCallback(
    (id: string) => {
      flatDeleteLineItem(id);
      if (selectedItemId === id) {
        setSelectedItemId(null);
      }
      setDirty(true);
    },
    [flatDeleteLineItem, selectedItemId],
  );

  const handleLineItemCategoryChange = useCallback(
    (id: string, category: string) => {
      flatUpdateLineItem(id, (item) => ({ ...item, category }));
      setDirty(true);
    },
    [flatUpdateLineItem],
  );

  const handleLineItemDescriptionChange = useCallback(
    (id: string, description: string) => {
      flatUpdateLineItem(id, (item) => ({ ...item, description }));
      setDirty(true);
    },
    [flatUpdateLineItem],
  );

  const handleLineItemDateChange = useCallback(
    (id: string, itemDate: string) => {
      flatUpdateLineItem(id, (item) => ({ ...item, itemDate: itemDate || undefined }));
      setDirty(true);
    },
    [flatUpdateLineItem],
  );

  // --------------------------------------------------------------------------
  // Sub-item handlers (operate on the selected line item)
  // --------------------------------------------------------------------------
  const handleSubItemsChange = useCallback(
    (subItems: SubItemForm[]) => {
      if (!selectedItemId) return;
      if (selectedSubItemId && !subItems.some((s) => s.id === selectedSubItemId)) {
        setSelectedSubItemId(null);
      }
      flatUpdateLineItem(selectedItemId, (item) => ({ ...item, subItems }));
      setDirty(true);
    },
    [flatUpdateLineItem, selectedItemId, selectedSubItemId],
  );

  const handleAddSubItem = useCallback(
    (type: string) => {
      if (!selectedItemId) return;
      const newItem = createSubItem(type, typeMeta);
      setSelectedSubItemId(newItem.id);
      flatUpdateLineItem(selectedItemId, (item) => ({
        ...item,
        subItems: [...item.subItems, newItem],
      }));
      setDirty(true);
    },
    [flatUpdateLineItem, selectedItemId, typeMeta],
  );

  // --------------------------------------------------------------------------
  // Project handlers
  // --------------------------------------------------------------------------
  const handleAddProject = useCallback(() => {
    const newProjectId = crypto.randomUUID();
    const sortOrder = flatItems.filter((i) => i.projectId === null).length;
    const newProject: ProjectData = {
      id: newProjectId,
      name: 'New Project',
      sortOrder,
      lineItems: [],
    };
    flatAddProject(newProject, selectedItemId ?? undefined);
    setDirty(true);
  }, [flatAddProject, flatItems, selectedItemId]);

  const handleRenameProject = useCallback(
    (projectId: string, name: string) => {
      flatRenameProject(projectId, name);
      setDirty(true);
    },
    [flatRenameProject],
  );

  const handleDeleteProject = useCallback(
    (projectId: string) => {
      flatDeleteProject(projectId);
      setDirty(true);
    },
    [flatDeleteProject],
  );

  const handleAddLineItemToProject = useCallback(
    (projectId: string) => {
      const newItemId = crypto.randomUUID();
      const newItem: LineItemData = {
        id: newItemId,
        category: '',
        description: '',
        totalCost: 0,
        subItems: [],
      };
      flatAddLineItem(newItem, projectId);
      setSelectedItemId(newItemId);
      setDirty(true);
    },
    [flatAddLineItem],
  );

  // --------------------------------------------------------------------------
  // Rates & rounding
  // --------------------------------------------------------------------------
  const handleRatesChange = useCallback(
    (newRates: { taxRate: number; markupRate: number }) => {
      setRates(newRates);
      setDirty(true);
    },
    [],
  );

  const handleSetRounding = useCallback((precision: number | null) => {
    setRoundingPrecision(precision);
    setDirty(true);
  }, []);

  // --------------------------------------------------------------------------
  // DnD drag-end handlers — signatures match BreakdownDndWrapper props.
  // The flat-array reorder transforms live in useFlatItems (moveItem /
  // moveProject); here we just mark dirty. Patch emission (getDndPatches) is
  // intentionally dropped — templates persist via full-replace on save.
  // --------------------------------------------------------------------------
  const handleDragEnd = useCallback(
    (activeId: string, overItem: FlatItem, isCollapsedProject: boolean) => {
      const result = moveItem(activeId, overItem, isCollapsedProject);
      if (result) setDirty(true);
    },
    [moveItem],
  );

  const handleProjectDragEnd = useCallback(
    (activeId: string, overItem: FlatItem) => {
      const result = moveProject(activeId, overItem);
      if (result) setDirty(true);
    },
    [moveProject],
  );

  const handleMoveToEnd = useCallback(
    (activeId: string) => {
      const result = moveItemToEnd(activeId);
      if (result) setDirty(true);
    },
    [moveItemToEnd],
  );

  return {
    // state
    flatItems,
    rates,
    roundingPrecision,
    selectedItemId,
    selectedSubItemId,
    dirty,
    // DnD support state passed through from useFlatItems (needed by the widget)
    collapsedProjects,
    getVisibleSortableIds,
    // selection / lifecycle
    setSelectedItemId,
    setSelectedSubItemId,
    handleToggleExpand,
    reset,
    markSaved,
    // line item handlers
    handleAddLineItem,
    handleDeleteLineItem,
    handleLineItemCategoryChange,
    handleLineItemDescriptionChange,
    handleLineItemDateChange,
    // sub-item handlers
    handleSubItemsChange,
    handleAddSubItem,
    // project handlers
    handleAddProject,
    handleRenameProject,
    handleDeleteProject,
    handleAddLineItemToProject,
    // rates / rounding
    handleRatesChange,
    handleSetRounding,
    // DnD handlers
    handleDragEnd,
    handleProjectDragEnd,
    handleMoveToEnd,
    toggleCollapse,
    startProjectDrag,
    cancelProjectDrag,
  };
}
